import Api from '../api';
import {
  DASHBOARD_DETAILS,
  DASHBOARD_SMS_STATS,
  DASHBOARD_SMS_STATS_URL,
  GET_MY_MESSAGE_URL,
} from '../lib/constant';
import {handleRejectCatch} from '../lib/utils';

// Stub data for dashboard details until balance API is integrated
export function dashboardDetails(){
  return (dispatch) => {
    const stubData = {
      success: true,
      data: {
        smsCount: {
          TRANS: 140000,
          PROMO: 100000,
          OTP: 180000
        },
        senderIds: ['VMTEST', 'TMTEST'],
        defaultSenderId: 'VMTEST',
        smsTypeTaken: ['Transactional', 'Promotional', 'OTP'],
        lastActivity: {},
        contactCount: 0,
        todayDeliveryData: []
      }
    };
    dispatch({type: DASHBOARD_DETAILS, payload: stubData});
  };
}

// Get user's timezone (defaults to Asia/Kolkata)
function getUserTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';
  } catch (e) {
    return 'Asia/Kolkata';
  }
}

// Backend returns int64 ms as a JSON string via grpc_json_transcoder
// (e.g. "1715760000000"). new Date(stringOfDigits) is Invalid Date, so coerce.
function toEpochMs(val) {
  if (val == null || val === '') return null;
  const n = typeof val === 'number' ? val : Number(val);
  return Number.isFinite(n) && n > 0 ? n : null;
}

// Pull the best-available timestamp off a single-message response.
// `preferred` is the field that semantically matches the activity (sentAt for
// outbound, deliveredAt for inbound). Falls back to createdAt so we still
// render something if the preferred field is unset.
function pickTimestamp(msg, preferred) {
  if (!msg) return null;
  return (
    toEpochMs(msg[preferred]) ||
    toEpochMs(msg.createdAt) ||
    toEpochMs(msg[preferred.replace(/[A-Z]/g, (c) => '_' + c.toLowerCase())]) ||
    toEpochMs(msg.created_at)
  );
}

// lastActivitySent/Received are MESSAGE ids whose campaign_id differs, so the
// campaign-scoped URL (with the message id in both slots) 404s. Resolve the
// message by id alone via GET /api/v1/me/messages/{message_id} (GetMyMessage),
// which needs no campaign_id.
// Returns {ts, text} — text=1 makes the API include sms_text in one call.
function fetchMessageActivity(activityId, preferredField) {
  if (!activityId) return Promise.resolve({ts: null, text: ''});
  const encoded = encodeURIComponent(activityId);
  const url = GET_MY_MESSAGE_URL.replace('{message_id}', encoded);
  return Api.get(url, true, {text: 1})
    .then((response) => {
      const body = response?.data;
      const msg = body?.message || body?.data?.message || body?.data || body;
      return {
        ts: pickTimestamp(msg, preferredField),
        text: msg?.messageText || msg?.message_text || '',
      };
    })
    .catch((err) => {
      console.warn('LastActivity message lookup failed:', activityId, err?.message || err);
      return {ts: null, text: ''};
    });
}

// Dashboard SMS stats — calls GET /api/v1/me/dashboard with timezone.
// lastActivitySent/Received now come back as message IDs (UUIDs); resolve them
// to timestamps via /api/v1/messages/{id} before handing off to the reducer.
export function dashboardSMSStats(){
  return (dispatch) => {
    const timezone = getUserTimezone();
    const emit = (data) => dispatch({type: DASHBOARD_SMS_STATS, payload: {success: true, data}});

    Api.get(DASHBOARD_SMS_STATS_URL, true, { timezone })
      .then((response) => {
        const data = response?.data;
        if (!data?.success || !data?.data) {
          emit({
            hours: {}, days: {}, months: {}, year: {},
            lastActivitySent: '', lastActivityReceived: '', lastActivity: {},
          });
          return;
        }
        const dashData = data.data;
        const sentId = dashData.lastActivitySent || '';
        const receivedId = dashData.lastActivityReceived || '';

        Promise.all([
          fetchMessageActivity(sentId, 'sentAt'),
          fetchMessageActivity(receivedId, 'deliveredAt'),
        ]).then(([sent, received]) => {
          const lastActivity = sent.ts ? { createdAt: sent.ts } : {};
          emit({
            hours: dashData.hours || {},
            days: dashData.days || {},
            months: dashData.months || {},
            year: dashData.year || {},
            lastActivitySent: sent.ts || '',
            lastActivityReceived: received.ts || '',
            lastActivitySentText: sent.text || '',
            lastActivityReceivedText: received.text || '',
            lastActivity,
          });
        });
      })
      .catch((err) => {
        console.error('Dashboard stats API error:', err);
        emit({
          hours: {}, days: {}, months: {}, year: {},
          lastActivitySent: '', lastActivityReceived: '', lastActivity: {},
        });
      });
  };
}
